import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDataEngine, useDataQuery } from '@dhis2/app-runtime';
import {
    SingleSelect, SingleSelectOption, NoticeBox,
    CircularLoader, AlertBar, InputField, Button, MultiSelect, MultiSelectOption
} from '@dhis2/ui';
import { STAFF_ROLES_ID, ROLE_LEVEL_MAP } from '../api/constants';
import {
    fetchOrgUnitTree,
    verifyUsername, createTEIOnly,
    checkUserExistsByUsername, sendCustomInvitation
} from '../../../utils/api';
import { updateStaffComplete } from '../api/staffQueries';
import { POSITION_ATTRIBUTES } from '../api/constants';


const optionSetsQuery = {
    genderSet: {
        resource: 'optionSets',
        params: {
            filter: 'name:eq:Gender',
            fields: 'options[id,code,name]',
            paging: 1000
        }
    }
};

const DEPARTMENT_ATTRIBUTE_UID = 'HZG6mhL4FL5';

let isMounted = true;

const departmentAttrId = 'HZG6mhL4FL5';

const StaffForm = ({ mode = 'create', onSubmit, onSuccess, initialData = {}, onCancel }) => {
    const engine = useDataEngine();
    const hasSetOrgUnit = useRef(false);
    const safeInitialData = initialData || {};
    const [selectedPosition, setSelectedPosition] = useState(null);

    const [notification, setNotification] = useState({
        visible: false,
        type: '',
        title: '',
        details: '',
        invitationUrl: null,
        showCopyButton: false,
        message: ''
    });

    const notificationTimeout = useRef();
    const isMountedRef = useRef(true);
    const [isInitialized, setIsInitialized] = useState(false);


    const [currentPositionInfo, setCurrentPositionInfo] = useState(null);
    const [loadingPositionInfo, setLoadingPositionInfo] = useState(false);

    const [generatedInvitationUrl, setGeneratedInvitationUrl] = useState(null);
    const [showInvitationUrl, setShowInvitationUrl] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);


    const showNotification = (type, title, details, options = {}) => {
            if (notificationTimeout.current) {
                clearTimeout(notificationTimeout.current);
            }

            const validTypes = ['success', 'warning', 'error', 'info'];
            const finalType = validTypes.includes(type) ? type : 'info';

            setNotification({
                visible: true,
                type: finalType,
                title,
                details,
                invitationUrl: options.invitationUrl || null,
                showCopyButton: options.showCopyButton || false,
                message: options.message || ''
            });

            notificationTimeout.current = setTimeout(() => {
                setNotification(prev => ({ ...prev, visible: false }));
            }, options.duration || 4000);
     };

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (notificationTimeout.current) {
                clearTimeout(notificationTimeout.current);
            }
        };
    }, []);


    const [formData, setFormData] = useState({
        id: safeInitialData.id || '',
        userId: safeInitialData.userId || '',
        firstName: safeInitialData.firstName || '',
        secondName: safeInitialData.secondName || '',
        gender: safeInitialData.gender || '',
        phoneNumber: safeInitialData.phoneNumber || '',
        email: safeInitialData.email || '',
        username: safeInitialData.username || '',
        orgUnit: safeInitialData.orgUnit || '',
        orgUnitName: safeInitialData.orgUnitName || '',
        // ✅ ADD THESE MISSING FIELDS:
        userRole: safeInitialData.userRole || safeInitialData.userRoles?.[0] || '', // For single role selection
        userRoles: Array.isArray(safeInitialData.userRoles)
            ? safeInitialData.userRoles
            : safeInitialData.userRole
                ? [safeInitialData.userRole]
                : [],
        department: safeInitialData.department || '',
        positionId: safeInitialData.positionId || safeInitialData.assignedToPosition || '',
        attributes: safeInitialData.attributes || [],
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState(true);
    const [usernameNotification, setUsernameNotification] = useState(null);
    const [checkingUsername, setCheckingUsername] = useState(false);

    // State for organisation units and departments
    const [allOrgUnits, setAllOrgUnits] = useState([]);
    const [filteredOrgUnits, setFilteredOrgUnits] = useState([]);
    const [orgUnitTree, setOrgUnitTree] = useState(null);
    const [cascadeSelections, setCascadeSelections] = useState({});
    const [isEditMode] = useState(mode === 'edit');
    const [orgUnitsLoaded, setOrgUnitsLoaded] = useState(false);
    const [vacantPositions, setVacantPositions] = useState([]);
    const [userOrgUnitId, setUserOrgUnitId] = useState(null);

    const { data: opts, loading: optsLoading, error: optsError } = useDataQuery(optionSetsQuery);
    const genderOps = opts?.genderSet?.optionSets?.[0]?.options || [];

    const styles = {
        positionChip: {
            padding: '12px 16px',
            backgroundColor: '#e8f4fd',
            borderRadius: '8px',
            border: '2px solid #4a90e2',
            marginBottom: '12px',
            transition: 'all 0.3s ease'
        },
        positionTitle: {
            fontWeight: 'bold',
            fontSize: '1rem',
            color: '#2c3e50',
            marginBottom: '4px'
        },
        positionId: {
            fontSize: '0.9rem',
            color: '#555',
            fontFamily: 'monospace'
        },
        statusBadge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            marginLeft: '8px'
        },
        vacantBadge: {
            backgroundColor: '#ff6b6b',
            color: 'white'
        },
        occupiedBadge: {
            backgroundColor: '#4CAF50',
            color: 'white'
        }
    };

    // Function to copy text to clipboard
        const copyToClipboard = async (text) => {
            try {
                await navigator.clipboard.writeText(text);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
                return true;
            } catch (err) {
                console.error('Failed to copy:', err);
                return false;
            }
        };

        // Function to manually copy invitation URL
        const handleCopyInvitationUrl = () => {
            if (generatedInvitationUrl) {
                copyToClipboard(generatedInvitationUrl);
                showNotification('success', 'Copied!', 'Invitation URL copied to clipboard');
            }
        };

    // Function to fetch current position information
    const fetchCurrentPositionInfo = useCallback(async (positionId) => {
        console.log('🔍 ========== fetchCurrentPositionInfo START ==========');
        console.log('🎯 Position ID received:', positionId);
        console.log('📊 Current formData.positionId:', formData.positionId);
        console.log('📊 Current selectedPosition:', selectedPosition);

        if (!positionId) {
            console.log('🚫 ERROR: No position ID provided to fetchCurrentPositionInfo');
            setCurrentPositionInfo(null);
            return null;
        }

        setLoadingPositionInfo(true);
        try {
            console.log('📡 Making API call to fetch position TEI:', positionId);

            const { position } = await engine.query({
                position: {
                    resource: `trackedEntityInstances/${positionId}`,
                    params: {
                        fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
                    }
                }
            });

            console.log('✅ API Response received:', position);

            if (!position) {
                console.warn('⚠️ Position TEI not found in response for ID:', positionId);
                // Create a minimal position info object
                const minimalInfo = {
                    teiId: positionId,
                    title: 'Position',
                    positionId: positionId,
                    orgUnit: formData.orgUnit,
                    isVacant: true,
                    currentIncumbent: null
                };
                console.log('📝 Setting minimal position info:', minimalInfo);
                setCurrentPositionInfo(minimalInfo);
                return minimalInfo;
            }

            const attrs = extractAttributes(position.attributes);
            console.log('📋 Extracted attributes:', attrs);

            const positionInfo = {
                teiId: position.trackedEntityInstance,
                title: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Position',
                positionId: attrs[POSITION_ATTRIBUTES.POSITION_ID] || position.trackedEntityInstance,
                orgUnit: position.orgUnit,
                isVacant: !attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] ||
                    attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT].trim() === '',
                currentIncumbent: attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || null
            };

            console.log('✅ Position info object created:', positionInfo);
            console.log('📝 Setting currentPositionInfo state');
            setCurrentPositionInfo(positionInfo);
            console.log('✅ ========== fetchCurrentPositionInfo SUCCESS ==========');
            return positionInfo;

        } catch (error) {
            console.error('❌ ERROR in fetchCurrentPositionInfo:', error);
            console.error('❌ Error details:', {
                message: error.message,
                status: error.status,
                response: error.response
            });

            // Even on error, create a basic position info object
            const errorInfo = {
                teiId: positionId,
                title: 'Position (Error Loading)',
                positionId: positionId,
                orgUnit: formData.orgUnit,
                isVacant: true,
                currentIncumbent: null,
                error: true,
                errorMessage: error.message
            };

            console.log('⚠️ Using error fallback position info:', errorInfo);
            setCurrentPositionInfo(errorInfo);
            console.log('❌ ========== fetchCurrentPositionInfo FAILED ==========');
            return errorInfo;
        } finally {
            console.log('🏁 Setting loadingPositionInfo to false');
            setLoadingPositionInfo(false);
        }
    }, [engine, formData.orgUnit]);

    // Function to fetch position data for a staff member
    // Replace the existing fetchStaffPositionData with this version
const fetchStaffPositionData = useCallback(async (userId, staffTeiId) => {
    // Try by userId first (preferred)
    if (userId) {
        console.log('🔍 Fetching position for user ID:', userId);
        try {
            const { positions } = await engine.query({
                positions: {
                    resource: 'trackedEntityInstances',
                    params: {
                        trackedEntityType: 'UGGo0Ebjl0E',
                        filter: `V66IFsw5kq6:eq:${userId}`,
                        ouMode: 'ALL',               // ✅ Use ALL to avoid org unit restrictions
                        fields: 'trackedEntityInstance,attributes[attribute,value]',
                        pageSize: 1
                    }
                }
            });
            if (positions.trackedEntityInstances && positions.trackedEntityInstances.length > 0) {
                const positionTei = positions.trackedEntityInstances[0];
                console.log('✅ Found position for user:', positionTei.trackedEntityInstance);
                return positionTei.trackedEntityInstance;
            }
        } catch (error) {
            console.warn('⚠️ Position fetch by user ID failed:', error.message);
        }
    }

    // Fallback: try by staff TEI ID (if provided)
    if (staffTeiId) {
        console.log('🔍 Fallback: fetching position for staff TEI:', staffTeiId);
        try {
            const { positions } = await engine.query({
                positions: {
                    resource: 'trackedEntityInstances',
                    params: {
                        trackedEntityType: 'UGGo0Ebjl0E',
                        filter: `V66IFsw5kq6:eq:${staffTeiId}`,
                        ouMode: 'ALL',
                        fields: 'trackedEntityInstance,attributes[attribute,value]',
                        pageSize: 1
                    }
                }
                });
                if (positions.trackedEntityInstances && positions.trackedEntityInstances.length > 0) {
                    const positionTei = positions.trackedEntityInstances[0];
                    console.log('✅ Found position via TEI fallback:', positionTei.trackedEntityInstance);
                    return positionTei.trackedEntityInstance;
                }
            } catch (error) {
                console.warn('⚠️ Position fetch by staff TEI failed:', error.message);
            }
        }

        console.log('❌ No position found for this staff');
        return null;
    }, [engine]);


    // Helper function to get current user's org unit (level 5)
    const getCurrentUserOrgUnit = async () => {
        try {
            console.log('🔍 Getting current user org unit');

            // Get current user's details including their org units
            const { me } = await engine.query({
                me: {
                    resource: 'me',
                    params: {
                        fields: 'organisationUnits[id,name,level]'
                    }
                }
            });

            if (me.organisationUnits && me.organisationUnits.length > 0) {
                // Get the first (primary) org unit of the current user
                const userOrgUnit = me.organisationUnits[0];

                console.log('🎯 Found current user org unit:', {
                    id: userOrgUnit.id,
                    name: userOrgUnit.name,
                    level: userOrgUnit.level
                });

                return {
                    id: userOrgUnit.id,
                    name: userOrgUnit.name,
                    level: userOrgUnit.level
                };
            } else {
                console.log('⚠️ Current user has no org units assigned');
                return null;
            }

        } catch (error) {
            console.error('❌ Failed to get current user org unit:', error);
            return null;
        }
    };

   const sendUserInvitation = async (userData, topLevelOrgUnit = null) => {
        try {
            console.log('📧 Sending invitation via Node.js backend...');
            
            const API_BASE_URL = process.env.REACT_APP_API_URL || 
                (process.env.NODE_ENV === 'production' 
                ? '/backend-api'  // ✅ Relative path (uses same protocol as page)
                : 'http://localhost:3001');

            // Get current user's org unit if not provided
            let currentUserOrgUnit = topLevelOrgUnit;
            if (!currentUserOrgUnit) {
                currentUserOrgUnit = await getCurrentUserOrgUnit();
            }

            // Prepare org unit arrays
            const organisationUnits = [];
            const dataViewOrganisationUnits = [];

            // Add selected org unit
            if (userData.orgUnit) {
                organisationUnits.push({ id: userData.orgUnit });
                dataViewOrganisationUnits.push({ id: userData.orgUnit });
            }

            // Add current user's org unit if different
            if (currentUserOrgUnit && currentUserOrgUnit.id !== userData.orgUnit) {
                console.log('➕ Adding current user org unit:', currentUserOrgUnit.name);
                organisationUnits.push({ id: currentUserOrgUnit.id });
                dataViewOrganisationUnits.push({ id: currentUserOrgUnit.id });
            }

            const payload = {
                firstName: userData.firstName,
                surname: userData.surname || userData.secondName || '',
                email: userData.email,
                username: userData.username,
                userRoles: userData.userRoles || (userData.userRole ? [userData.userRole] : []),
                organisationUnits: organisationUnits,
                dataViewOrganisationUnits: dataViewOrganisationUnits,
                redirectUrl: window.location.origin + '/set-password',
                sendEmail: true,
                expiryDays: 4
            };

            console.log('📤 Sending to backend:', payload);

            const response = await fetch(`${API_BASE_URL}/api/users/invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to send invitation');
            }

            const result = await response.json();
            console.log('✅ Invitation sent via backend:', result);

            // Store the invitation URL for manual copying
            if (result.data?.invitationUrl) {
                setGeneratedInvitationUrl(result.data.invitationUrl);
                setShowInvitationUrl(true);
            }

            return {
                success: true,
                userId: result.data?.userId,
                invitationUrl: result.data?.invitationUrl,
                emailSent: result.data?.emailSent
            };

        } catch (error) {
            console.error('❌ Failed to send invitation:', error);
            throw error;
        }
    };

    const handleResendInvitation = async () => {
        console.log('[DEBUG] handleResendInvitation called');
        console.log('[DEBUG] formData:', formData);
        console.log('[DEBUG] formData.userId:', formData.userId);
        console.log('[DEBUG] formData.email:', formData.email);
        console.log('[DEBUG] formData.username:', formData.username);

        if (!formData.userId || !formData.email) {
            showNotification('error', 'Cannot resend', 'User data incomplete');
            return;
        }

        setLoading(true);
        try {
            // ✅ USE THE NODE.JS BACKEND
            const API_BASE_URL = process.env.REACT_APP_API_URL || 
                (process.env.NODE_ENV === 'production' 
                ? '/backend-api'  // ✅ Relative path (uses same protocol as page)
                : 'http://localhost:3001');
                            
            console.log('📧 Resending invitation via Node.js backend...');
            
            const response = await fetch(`${API_BASE_URL}/api/users/${formData.userId}/resend-invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: formData.email,
                    firstName: formData.firstName,
                    surname: formData.secondName,
                    username: formData.username,
                    userRoles: formData.userRoles || [],
                    redirectUrl: window.location.origin + '/set-password'
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to resend invitation');
            }

            const result = await response.json();
            console.log('✅ Invitation resent via backend:', result);

            // Store the invitation URL for manual copying
            if (result.data?.invitationUrl) {
                setGeneratedInvitationUrl(result.data.invitationUrl);
                setShowInvitationUrl(true);
                
                showNotification('success', 'Invitation Resent', 
                    `Invitation email resent to ${formData.email}.`,
                    {
                        invitationUrl: result.data.invitationUrl,
                        showCopyButton: true,
                        duration: 10000
                    }
                );
            } else {
                showNotification('success', 'Invitation Resent', 
                    `Invitation email resent to ${formData.email}`,
                    { duration: 5000 }
                );
            }

        } catch (error) {
            console.error('❌ Resend failed:', error);
            showNotification('error', 'Resend Failed', error.message || 'Could not process request.');
        } finally {
            setLoading(false);
        }
    };

    // Wrap validateUsername in useCallback to prevent infinite re-renders
    const validateUsername = useCallback(async (username) => {
        console.log('🔍 validateUsername called with:', username);

        if (username.length >= 4) {
            setCheckingUsername(true);
            try {
                console.log('📞 Calling verifyUsername API...');
                const available = await verifyUsername(username, false);
                console.log('✅ Username availability result:', available);
                setUsernameAvailable(available);

                if (!available) {
                    setUsernameNotification({
                        type: 'error',
                        message: 'Username not available',
                        details: 'The username is already taken. Please choose another one.'
                    });
                } else {
                    setUsernameNotification(null);
                }
            } catch (error) {
                console.error('❌ Username check failed:', error);
                setUsernameNotification({
                    type: 'error',
                    message: 'Username verification unavailable',
                    details: 'Unable to verify username availability. Please try again.'
                });
                setUsernameAvailable(true);
            } finally {
                setCheckingUsername(false);
            }
        } else if (username.length > 0) {
            console.log('⚠️ Username too short:', username.length);
            setUsernameNotification({
                type: 'error',
                message: 'Invalid username',
                details: 'Username must be at least 4 characters'
            });
            setUsernameAvailable(false);
        } else {
            console.log('🗑️ Username cleared');
            setUsernameNotification(null);
            setUsernameAvailable(true);
        }
    }, []);

    useEffect(() => {
        if (formData.positionId && !currentPositionInfo && !loadingPositionInfo) {
            console.log('🔍 Force fetching position info for:', formData.positionId);
            fetchCurrentPositionInfo(formData.positionId);
        }
    }, [formData.positionId, currentPositionInfo, loadingPositionInfo, fetchCurrentPositionInfo]);


    // Load current position info when positionId changes
    useEffect(() => {
        console.log('🔄 POSITION LOADING EFFECT TRIGGERED');
        console.log('📋 Effect inputs:', {
            formDataPositionId: formData.positionId,
            selectedPosition,
            mode
        });

        const loadCurrentPosition = async () => {
            const positionId = formData.positionId || selectedPosition;

            console.log('🔍 Position loading check:', {
                positionId,
                hasPositionId: !!positionId,
                formDataPositionId: formData.positionId,
                selectedPosition
            });

            if (positionId) {
                console.log('🎯 Calling fetchCurrentPositionInfo with ID:', positionId);
                await fetchCurrentPositionInfo(positionId);
            } else {
                console.log('🚫 No position ID to load, setting currentPositionInfo to null');
                setCurrentPositionInfo(null);
            }
        };

        // Load position info whenever we have a positionId
        if (formData.positionId || selectedPosition) {
            console.log('🚀 Starting position load...');
            loadCurrentPosition();
        } else {
            console.log('⏸️ Skipping position load - no positionId or selectedPosition');
        }
    }, [formData.positionId, selectedPosition, fetchCurrentPositionInfo]);

    useEffect(() => {
        console.log('🔍 [DEBUG POSITION******************** SELECTION]', {
            selectedPosition,
            formDataPositionId: formData.positionId,
            vacantPositionsCount: vacantPositions.length,
            vacantPositionsValues: vacantPositions.map(p => ({
                tei: p.tei,
                label: p.label
            })),
            isSelectedInOptions: selectedPosition
                ? vacantPositions.some(p => p.tei === selectedPosition)
                : 'none'
        });

        // Check if selectedPosition matches any position TEI format
        if (selectedPosition) {
            const isTeiFormat = /^[a-zA-Z0-9]{11}$/.test(selectedPosition);
            console.log('📝 Selected value analysis:', {
                value: selectedPosition,
                length: selectedPosition.length,
                isTeiFormat,
                isOrgUnitId: selectedPosition === 'XNRw9SCpmDb' ? 'YES!' : 'no'
            });
        }
    }, [selectedPosition, formData.positionId, vacantPositions]);


    //===========TO BE REMOVE

    useEffect(() => {
        console.log('🔍 POSITION ID ANALYSIS:', {
            formDataPositionId: formData.positionId,
            selectedPosition,
            hasFormDataPositionId: !!formData.positionId,
            hasSelectedPosition: !!selectedPosition,
            safeInitialDataPositionId: safeInitialData.positionId,
            safeInitialDataAssignedToPosition: safeInitialData.assignedToPosition,
            mode,
            isEditMode
        });

        // Check if we should be fetching position info
        const shouldFetch = formData.positionId || selectedPosition;
        console.log('🎯 Should fetch position info?', shouldFetch);
        console.log('🎯 Current position ID to use:', formData.positionId || selectedPosition);

    }, [formData.positionId, selectedPosition, safeInitialData, mode, isEditMode]);

    {
        useEffect(() => {
            if (selectedPosition && vacantPositions.length > 0) {
                const isValid = vacantPositions.some(
                    pos => pos.trackedEntityInstance === selectedPosition
                );
                if (!isValid) {
                    console.warn(`Position ${selectedPosition} not in vacant positions - clearing`);
                    setSelectedPosition('');
                    setFormData(prev => ({ ...prev, positionId: '' }));
                }
            }
        }, [selectedPosition, vacantPositions]);
    }

    useEffect(() => {
        if (safeInitialData && !hasLoadedInitialData.current) {
            console.log('📥 Loading initial data');

            // First, extract position ID from direct data if available
            let positionId = safeInitialData.positionId || safeInitialData.assignedToPosition || '';

            // If no position ID in direct data, check attributes
            if (!positionId && safeInitialData.attributes) {
                const positionAttr = safeInitialData.attributes.find(
                    attr => attr.attribute === POSITION_ATTRIBUTES.POSITION_ID
                );
                positionId = positionAttr?.value || '';
            }

            console.log('🎯 Position ID from initial data:', positionId || 'NOT FOUND');

            // Set form data (including empty positionId if not found)
            setFormData(prev => ({
                ...prev,
                id: safeInitialData.id || '',
                userId: safeInitialData.userId || '',
                username: safeInitialData.username || '',
                firstName: safeInitialData.firstName || '',
                secondName: safeInitialData.secondName || '',
                email: safeInitialData.email || '',
                phoneNumber: safeInitialData.phoneNumber || '',
                gender: safeInitialData.gender || '',
                dob: safeInitialData.dob || '',
                userRole: safeInitialData.userRole || safeInitialData.userRoles?.[0] || '',
                userRoles: Array.isArray(safeInitialData.userRoles)
                    ? safeInitialData.userRoles
                    : safeInitialData.userRole
                        ? [safeInitialData.userRole]
                        : [],
                department: safeInitialData.department || '',
                positionId: positionId, // This might be empty
                assignedToPosition: positionId,
                orgUnit: safeInitialData.orgUnit || '',
                orgUnitName: safeInitialData.orgUnitName || ''
            }));

            // If we have a staff TEI ID but no position ID, try to fetch it
             if (safeInitialData.userId && !positionId) {
                console.log('🔍 Staff has user ID but no position ID - fetching position data...');
                fetchStaffPositionData(safeInitialData.userId, safeInitialData.id).then(fetchedPositionId => {
                    if (fetchedPositionId) {
                        console.log('✅ Found position via query:', fetchedPositionId);
                        // Update form data with fetched position
                        setFormData(prev => ({
                            ...prev,
                            positionId: fetchedPositionId,
                            assignedToPosition: fetchedPositionId
                        }));
                        setSelectedPosition(fetchedPositionId);

                        // Fetch position details
                        fetchCurrentPositionInfo(fetchedPositionId);
                    }
                });
            } else if (positionId) {
                // If we already have a position ID, fetch its details
                console.log('🎯 Fetching position details for:', positionId);
                setSelectedPosition(positionId);
                fetchCurrentPositionInfo(positionId);
            }

            hasLoadedInitialData.current = true;
        }
    }, [safeInitialData, fetchStaffPositionData, fetchCurrentPositionInfo]);


    //==========UP TO HER==================

    useEffect(() => {
        if (orgUnitsLoaded && allOrgUnits.length > 0) {
            console.log('✅ Org units loaded, component ready');
            setIsInitialized(true);
        }
    }, [orgUnitsLoaded, allOrgUnits.length]);


    // Update this useEffect to check if we have a position from initial data
    useEffect(() => {
        const autoFillOrgUnitFromPosition = async () => {
            const positionToCheck = selectedPosition || formData.positionId;

            if (positionToCheck && (!formData.orgUnit || formData.orgUnit !== positionToCheck)) {
                console.log('🔍 Auto-filling org unit from position:', positionToCheck);

                try {
                    // Fetch position details
                    const { position } = await engine.query({
                        position: {
                            resource: `trackedEntityInstances/${positionToCheck}`,
                            params: {
                                fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
                            }
                        }
                    });

                    if (position && position.orgUnit) {
                        // Get org unit details
                        const { orgUnitInfo } = await engine.query({
                            orgUnitInfo: {
                                resource: `organisationUnits/${position.orgUnit}`,
                                params: {
                                    fields: 'id,name,level,path,parent[id,name]'
                                }
                            }
                        });

                        // Extract position title
                        const currentAttrs = extractAttributes(position.attributes);
                        const positionTitle = currentAttrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position';

                        console.log('✅ Auto-filled from position:', {
                            positionTitle,
                            orgUnit: orgUnitInfo.name,
                            orgUnitLevel: orgUnitInfo.level,
                            orgUnitId: position.orgUnit
                        });

                        // ✅ CRITICAL FIX: Ensure the org unit is in allOrgUnits
                        if (!allOrgUnits.find(ou => ou.id === position.orgUnit)) {
                            console.log('➕ Adding position org unit to available list');
                            setAllOrgUnits(prev => [...prev, {
                                id: position.orgUnit,
                                name: orgUnitInfo.name,
                                level: orgUnitInfo.level,
                                levelName: `Level ${orgUnitInfo.level}`,
                                parentId: orgUnitInfo.parent?.id || null,
                                path: orgUnitInfo.path
                            }]);
                        }

                        // ✅ CRITICAL FIX: Also add to filteredOrgUnits so SingleSelect can find it
                        if (!filteredOrgUnits.find(ou => ou.id === position.orgUnit)) {
                            setFilteredOrgUnits(prev => [...prev, {
                                id: position.orgUnit,
                                name: orgUnitInfo.name,
                                level: orgUnitInfo.level,
                                levelName: `Level ${orgUnitInfo.level}`,
                                parentId: orgUnitInfo.parent?.id || null,
                                path: orgUnitInfo.path
                            }]);
                        }

                        // Update form data
                        setFormData(prev => ({
                            ...prev,
                            orgUnit: position.orgUnit,
                            orgUnitName: orgUnitInfo.name,
                            positionId: positionToCheck
                        }));

                        // Clear any org unit errors
                        setErrors(prev => ({ ...prev, orgUnit: null }));

                        // Show notification
                        showNotification('success', 'Position Pre-filled',
                            `${positionTitle} - Department set to: ${orgUnitInfo.name} (Level ${orgUnitInfo.level})`);
                    }
                } catch (error) {
                    console.warn('⚠️ Could not auto-fill department from position:', error);
                }
            }
        };

        autoFillOrgUnitFromPosition();
    }, [selectedPosition, formData.positionId, engine, allOrgUnits, filteredOrgUnits]);

    // Add this useEffect to check username when position changes
    useEffect(() => {
        const checkPositionStaff = async () => {
            if (selectedPosition && formData.username) {
                try {
                    const { position } = await engine.query({
                        position: {
                            resource: `trackedEntityInstances/${selectedPosition}`,
                            params: {
                                fields: 'attributes[attribute,value]'
                            }
                        }
                    });

                    const currentAttrs = extractAttributes(position.attributes);
                    const existingStaffUserId = currentAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];

                    if (existingStaffUserId && existingStaffUserId.trim() !== '') {
                        // Get the username for this user ID
                        try {
                            const userResponse = await engine.query({
                                user: {
                                    resource: `users/${existingStaffUserId}`,
                                    params: { fields: 'userCredentials[username]' }
                                }
                            });

                            const existingUsername = userResponse.user?.userCredentials?.username;

                            if (existingUsername && existingUsername !== formData.username) {
                                console.log('⚠️ Position has different staff:', existingUsername);
                                showNotification('warning', 'Position Conflict',
                                    `This position is assigned to ${existingUsername}. Saving will reassign it to ${formData.username}.`);
                            }
                        } catch (userError) {
                            console.warn('Could not fetch username for user ID:', existingStaffUserId);
                        }
                    }
                } catch (error) {
                    console.warn('⚠️ Could not check position staff:', error);
                }
            }
        };

        checkPositionStaff();
    }, [selectedPosition, formData.username]);

    // Add this useEffect to handle edit mode initialization
    useEffect(() => {
        if (isEditMode && formData.userRole && allOrgUnits.length > 0 && orgUnitsLoaded) {
            console.log('🎯 Initializing cascade for edit mode with role:', formData.userRole);

            // Check if the current org unit exists in allOrgUnits
            if (formData.orgUnit && !allOrgUnits.find(ou => ou.id === formData.orgUnit)) {
                console.warn('⚠️ Current org unit not found in loaded org units:', formData.orgUnit);

                // Try to fetch the missing org unit
                const fetchMissingOrgUnit = async () => {
                    try {
                        const { orgUnit } = await engine.query({
                            orgUnit: {
                                resource: `organisationUnits/${formData.orgUnit}`,
                                params: { fields: 'id,name,level,parent[id]' }
                            }
                        });

                        if (orgUnit) {
                            // Add it to allOrgUnits
                            const newOrgUnit = {
                                id: orgUnit.id,
                                name: orgUnit.name,
                                level: orgUnit.level,
                                parentId: orgUnit.parent?.id || null
                            };

                            setAllOrgUnits(prev => [...prev, newOrgUnit]);
                            console.log('✅ Added missing org unit:', orgUnit.name);
                        }
                    } catch (error) {
                        console.warn('⚠️ Could not fetch missing org unit:', error);
                    }
                };

                fetchMissingOrgUnit();
            }

            // Initialize cascade
            initializeCascadeForEdit(allOrgUnits, formData.orgUnit, formData.userRole);
        }
    }, [isEditMode, formData.userRole, allOrgUnits.length, orgUnitsLoaded, formData.orgUnit]);

    // Username validation effect
    useEffect(() => {
        console.log('🎯 useEffect triggered - username:', formData.username, 'mode:', mode);

        const isCreateMode = mode === 'create' || mode === 'add';

        if (formData.username && isCreateMode) {
            console.log('⏰ Setting up validation timer...');
            const timer = setTimeout(() => {
                console.log('⏰ Timer expired, validating username...');
                validateUsername(formData.username);
            }, 500);
            return () => {
                console.log('🧹 Cleaning up previous timer');
                clearTimeout(timer);
            };
        } else {
            console.log('🚫 Skipping validation - username empty or edit mode');
            setUsernameNotification(null);
            setUsernameAvailable(true);
            setErrors((prev) => ({ ...prev, username: null }));
        }
    }, [formData.username, mode, validateUsername]);

    // Build hierarchical org unit structure
    const buildOrgUnitTree = (orgUnits) => {
        const tree = { children: [] };
        const map = {};

        // First pass: create map of all org units
        orgUnits.forEach(ou => {
            map[ou.id] = {
                ...ou,
                children: []
            };
        });

        // Second pass: build tree structure
        orgUnits.forEach(ou => {
            if (ou.parentId && map[ou.parentId]) {
                map[ou.parentId].children.push(map[ou.id]);
            } else {
                tree.children.push(map[ou.id]);
            }
        });

        return tree;
    };

    // Get org units at specific level with parent filtering
    const getOrgUnitsAtLevel = (targetLevel, parentId = null) => {
        if (!allOrgUnits.length) {
            console.log('❌ No org units available');
            return [];
        }

        console.log(`🔍 Getting org units for level ${targetLevel}, parentId: ${parentId}`);
        let filtered = allOrgUnits.filter(ou => ou.level === targetLevel);
        console.log(`📊 Found ${filtered.length} org units at level ${targetLevel}`);

        // ✅ ONLY filter by parent ID if explicitly provided
        if (parentId) {
            filtered = filtered.filter(ou => ou.parentId === parentId);
            console.log(`📊 After parent filtering (${parentId}): ${filtered.length} org units`);
        }
        // ✅ NO PATH FILTERING - allOrgUnits already contains ONLY user-accessible org units

        return filtered;
    };

    // Get available levels from org units
    const getAvailableLevels = () => {
        if (!allOrgUnits.length) {
            console.log('❌ No org units available for level detection');
            return [];
        }

        const levels = [...new Set(allOrgUnits.map(ou => ou.level))].sort((a, b) => a - b);
        console.log('📊 Detected available levels:', levels);

        return levels;
    };

    const getRoleName = (roleId) => {
        const role = STAFF_ROLES_ID.find(r => r.id === roleId);
        return role ? (role.label || role.name || roleId) : roleId;
    };

    // ✅ NEW: Validate level and provide fallback if required level doesn't exist
    const getEffectiveLevel = (roleId, availableLevels) => {
        const targetLevel = ROLE_LEVEL_MAP[roleId];
        if (!targetLevel) {
            console.warn('🎯 No level mapping found for role:', roleId);
            return null;
        }

        // ✅ FIX: Handle empty availableLevels (org units not loaded yet)
        if (!availableLevels || availableLevels.length === 0) {
            console.warn('🎯 No available levels yet (org units loading), returning null');
            return null;
        }

        // Check if target level exists
        if (availableLevels.includes(targetLevel)) {
            console.log(`✅ Level ${targetLevel} exists for role ${roleId}`);
            return targetLevel;
        }

        // Find highest available level that is <= targetLevel
        const validLevels = availableLevels.filter(l => l <= targetLevel);
        if (validLevels.length > 0) {
            const fallbackLevel = Math.max(...validLevels);
            console.warn(`⚠️ Level ${targetLevel} missing for role ${roleId}. Falling back to highest available: ${fallbackLevel}`);
            return fallbackLevel;
        }

        // No valid level found, use highest available overall
        const highestLevel = Math.max(...availableLevels);
        console.error(`❌ No valid level <= ${targetLevel} found. Using highest: ${highestLevel}`);
        return highestLevel;
    };


    // Handle role change
    const handleUserTypeChange = async (selectedRoleId) => {
        if (formData.userRole === selectedRoleId) {
            return;
        }
        const roleName = getRoleName(selectedRoleId);
        console.log('🎯 Role changed to:', selectedRoleId, '(', roleName, ')');

        setFormData(prev => ({
            ...prev,
            userRole: selectedRoleId,
            orgUnit: '',
            orgUnitName: ''
        }));

        // Reset cascade selections
        setCascadeSelections({});

        // ✅ NEW: Get effective level with fallback for missing levels
        const availableLevels = getAvailableLevels();
        const targetLevel = getEffectiveLevel(selectedRoleId, availableLevels);
        console.log('🎯 Effective level for role:', targetLevel);

        if (targetLevel && allOrgUnits.length > 0) {
            const levelsToShow = availableLevels.filter(level => level <= targetLevel);
            console.log('📊 Levels to show:', levelsToShow);

            if (levelsToShow.length > 1) {
                // Multi-level cascade
                const firstLevel = levelsToShow[0];
                const firstLevelOrgs = getOrgUnitsAtLevel(firstLevel);
                console.log(`📋 First level (${firstLevel}) orgs:`, firstLevelOrgs);
                setFilteredOrgUnits(firstLevelOrgs);
            } else {
                // Single level, show org units at target level
                // ✅ CRITICAL FIX: For level 6, only show org units under user's root org unit
                let filtered = [];
                if (targetLevel === 6 && userOrgUnitId) {
                    // Get the level 5 org unit under user's root
                    const allLevel5Orgs = getOrgUnitsAtLevel(5);
                    console.log('🔍 [DEBUG] All level 5 org units:', allLevel5Orgs.length);
                    const level5Orgs = allLevel5Orgs.filter(ou =>
                        ou.path && ou.path.includes(userOrgUnitId)
                    );
                    console.log('🔍 [DEBUG] userOrgUnitId:', userOrgUnitId);
                    console.log('🔍 [DEBUG] Level 5 orgs matching user hierarchy:', level5Orgs.length, level5Orgs.map(ou => ({ id: ou.id, name: ou.name })));

                    if (level5Orgs.length > 0) {
                        // Show level 6 org units under the first level 5 org unit
                        filtered = getOrgUnitsAtLevel(6, level5Orgs[0].id);
                        console.log('🔍 [DEBUG] Using parent level 5:', level5Orgs[0].name, level5Orgs[0].id);
                    } else {
                        // ✅ FIX: Filter ALL level 6 org units by user's root org unit
                        console.warn('⚠️ [DEBUG] No level 5 orgs found under user hierarchy. Filtering level 6 by userOrgUnitId path.');
                        const allLevel6Orgs = getOrgUnitsAtLevel(6);
                        filtered = allLevel6Orgs.filter(ou =>
                            ou.path && ou.path.includes(userOrgUnitId)
                        );
                        console.log('🔍 [DEBUG] Filtered level 6 org units by company:', filtered.length, 'out of', allLevel6Orgs.length);
                    }
                } else {
                    console.log('🔍 [DEBUG] targetLevel:', targetLevel, 'userOrgUnitId:', userOrgUnitId);
                    filtered = getOrgUnitsAtLevel(targetLevel);
                }

                console.log(`📋 Available org units at level ${targetLevel}:`, filtered.length);
                setFilteredOrgUnits(filtered);
            }
        } else {
            console.log('⚠️ No target level or org units available');
            setFilteredOrgUnits(allOrgUnits);
        }

    };


    const updatePositionLink = async (positionId, staffUsername) => {
        if (!positionId || !staffUsername) {
            console.log('⚠️ Missing positionId or staffUsername');
            return null;
        }

        try {
            console.log('🔗 Updating Position TEI for staff username:', staffUsername);

            // STEP 1: Get the backend User ID from the username
            console.log('🔍 Looking up DHIS2 user ID for username:', staffUsername);
            const userResponse = await engine.query({
                users: {
                    resource: 'users',
                    params: {
                        filter: `userCredentials.username:eq:${staffUsername}`,
                        fields: 'id,userCredentials[username],firstName,surname',
                        pageSize: 1
                    }
                }
            });

            if (!userResponse.users.users.length) {
                throw new Error(`User ${staffUsername} not found in database`);
            }

            const dhis2User = userResponse.users.users[0];
            const dhis2UserId = dhis2User.id;

            console.log('✅ Found user:', {
                userId: dhis2UserId,
                username: staffUsername,
                name: `${dhis2User.firstName} ${dhis2User.surname}`
            });

            // STEP 2: Get the current position TEI
            const { position } = await engine.query({
                position: {
                    resource: `trackedEntityInstances/${positionId}`,
                    params: {
                        fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
                    }
                }
            });

            if (!position || !position.attributes) {
                throw new Error('Position TEI not found');
            }

            // STEP 3: Update position attributes
            const currentAttrs = [...position.attributes];

            // Update CURRENT_INCUMBENT with User ID (V66IFsw5kq6)
            const currentIncumbentAttrIndex = currentAttrs.findIndex(
                attr => attr.attribute === POSITION_ATTRIBUTES.CURRENT_INCUMBENT
            );

            if (currentIncumbentAttrIndex !== -1) {
                currentAttrs[currentIncumbentAttrIndex].value = dhis2UserId;
                console.log('📝 Updated existing incumbent attribute with User ID:', dhis2UserId);
            } else {
                currentAttrs.push({
                    attribute: POSITION_ATTRIBUTES.CURRENT_INCUMBENT,
                    value: dhis2UserId
                });
                console.log('➕ Added new incumbent attribute with User ID:', dhis2UserId);
            }

            // Update STATUS to "Active" (Ch7TyL91heU)
            const statusAttrIndex = currentAttrs.findIndex(
                attr => attr.attribute === POSITION_ATTRIBUTES.POSITION_STATUS
            );

            if (statusAttrIndex !== -1) {
                currentAttrs[statusAttrIndex].value = "Active";
                console.log('📈 Updated position status to "Active"');
            } else {
                currentAttrs.push({
                    attribute: POSITION_ATTRIBUTES.POSITION_STATUS,
                    value: "Active"
                });
                console.log('➕ Added position status as "Active"');
            }

            // STEP 4: Save the updated position
            console.log('🔄 Saving updated position...');
            const response = await engine.mutate({
                resource: `trackedEntityInstances/${positionId}`,
                type: 'update',
                data: {
                    trackedEntityInstance: positionId,
                    orgUnit: position.orgUnit,
                    attributes: currentAttrs
                }
            });

            console.log('✅ Position updated successfully with User ID:', dhis2UserId);
            return response;

        } catch (error) {
            console.error('❌ Position update failed:', error);
            throw new Error(`Failed to update position: ${error.message}`);
        }
    };

    const getAvailableOptions = (levelOrgs, selectedValue) => {
        const options = [...levelOrgs];

        if (selectedValue && !options.some(ou => ou.id === selectedValue)) {
            // Try to find it in allOrgUnits first
            const selectedOrgUnit = allOrgUnits.find(ou => ou.id === selectedValue);

            if (selectedOrgUnit) {
                // Prepend to options so it appears first
                options.unshift(selectedOrgUnit);
                console.log('✅ Added selected org unit to options:', selectedOrgUnit.name);
            } else if (formData.orgUnitName) {
                // Fallback: Create a minimal option with just the name
                options.unshift({
                    id: selectedValue,
                    name: formData.orgUnitName,
                    level: 'N/A',
                    levelName: 'Level N/A'
                });
                console.warn('⚠️ Created placeholder option for missing org unit:', selectedValue);
            }
        }

        return options;
    };

    // Check if selected value exists in options
    const isValidSelection = (selectedValue, options) => {
        if (!selectedValue) return true; // Empty selection is valid
        return options.some(option => option.id === selectedValue);
    };

    const initializeCascadeForEdit = async (orgUnits, existingOrgUnitId, existingUserRole) => {
        console.log('🔍 Initializing cascade for edit:', { existingOrgUnitId, existingUserRole });

        // ✅ CRITICAL FIX: Don't check if existing org unit is in hierarchy on load
        // Only validate on save attempt
        const existingOrgUnit = orgUnits.find(ou => ou.id === existingOrgUnitId);

        // ✅ CRITICAL FIX: Silent reset if org unit is outside hierarchy (NO WARNING ON LOAD)
        if (!existingOrgUnit) {
            console.warn('⚠️ Existing org unit NOT in current hierarchy:', existingOrgUnitId);
            // Reset form data WITHOUT notification (validation will catch on save)
            setFormData(prev => ({
                ...prev,
                orgUnit: '',
                orgUnitName: ''
            }));
            // DO NOT return - continue to set up valid cascade options
        }

        // ✅ NEW: Get effective level with fallback for missing levels
        const availableLevels = getAvailableLevels();
        const targetLevel = getEffectiveLevel(existingUserRole, availableLevels);
        console.log('🎯 Effective level for role:', existingUserRole, '->', targetLevel);

        if (!targetLevel) {
            console.warn('No target level found for role:', existingUserRole);
            setFilteredOrgUnits(orgUnits);
            return;
        }

        const levelsToShow = availableLevels.filter(level => level <= targetLevel);
        console.log('📊 Available levels:', availableLevels, 'Levels to show:', levelsToShow);

        if (levelsToShow.length > 1) {
            // For cascade, we need to build the selection path
            const selections = {};

            // ✅ CRITICAL FIX: Only build path if org unit exists in hierarchy
            if (existingOrgUnit) {
                const pathIds = existingOrgUnit.path.split('/').filter(id => id.trim());
                console.log('🛣️ Org unit path IDs:', pathIds);

                // ✅ FIX: Find the correct path ID for each level, not by index
                levelsToShow.forEach((level) => {
                    // Find the path ID that corresponds to this level
                    const pathIdForLevel = pathIds.find((pathId, idx) => {
                        const pathOrgUnit = orgUnits.find(ou => ou.id === pathId);
                        return pathOrgUnit && pathOrgUnit.level === level;
                    });

                    if (pathIdForLevel) {
                        const orgUnitAtLevel = orgUnits.find(ou =>
                            ou.level === level && ou.id === pathIdForLevel
                        );
                        if (orgUnitAtLevel) {
                            selections[level] = orgUnitAtLevel.id;
                            console.log(`📍 Level ${level} selection:`, orgUnitAtLevel.name, orgUnitAtLevel.id);
                        }
                    } else {
                        console.log(`⚠️ No path ID found for level ${level} in path:`, pathIds);
                    }
                });

                console.log('✅ Built cascade selections by level matching:', selections);
            }

            console.log('🎯 Final cascade selections:', selections);
            setCascadeSelections(selections);

            // Set filtered org units for the first level
            const firstLevel = levelsToShow[0];
            const firstLevelOrgs = getOrgUnitsAtLevel(firstLevel);
            console.log(`📋 First level (${firstLevel}) orgs:`, firstLevelOrgs);
            setFilteredOrgUnits(firstLevelOrgs);

            // ✅ CRITICAL FIX: Filter subsequent levels by parent
            if (levelsToShow.includes(6) && selections[5]) {
                const level6Orgs = getOrgUnitsAtLevel(6, selections[5]);
                console.log(`📋 Level 6 orgs (filtered by parent ${selections[5]}):`, level6Orgs.length);
                setFilteredOrgUnits(level6Orgs);
            }

            // Ensure the final org unit is set in form data (only if valid)
            if (selections[targetLevel]) {
                const finalOrgUnit = orgUnits.find(ou => ou.id === selections[targetLevel]);
                if (finalOrgUnit) {
                    console.log('✅ Setting final org unit in form data:', finalOrgUnit.name, finalOrgUnit.id);
                    setFormData(prev => ({
                        ...prev,
                        orgUnit: finalOrgUnit.id,
                        orgUnitName: finalOrgUnit.name
                    }));
                }
            } else if (!existingOrgUnit) {
                // ✅ CRITICAL FIX: If org unit not in hierarchy, reset to empty (don't show warning yet)
                console.log('⚠️ Existing org unit not in hierarchy, resetting to empty');
                setFormData(prev => ({
                    ...prev,
                    orgUnit: '',
                    orgUnitName: ''
                }));
            }
        } else {
            // Single level, show org units at target level
            console.log('🔄 Setting up SINGLE LEVEL selection');

            // ✅ CRITICAL FIX: Filter by parent if we have path information
            let filtered = [];
            if (existingOrgUnit && existingOrgUnit.parentId) {
                filtered = getOrgUnitsAtLevel(targetLevel, existingOrgUnit.parentId);
                console.log(`📋 Org units at target level ${targetLevel} (parent ${existingOrgUnit.parentId}):`, filtered.length);
            } else {
                filtered = getOrgUnitsAtLevel(targetLevel);
                console.log(`📋 Org units at target level ${targetLevel}:`, filtered.length);
            }

            setFilteredOrgUnits(filtered);

            // ✅ CRITICAL FIX: Only set form data if org unit is valid
            if (existingOrgUnit) {
                const currentOrgInFiltered = filtered.find(ou => ou.id === existingOrgUnitId);
                if (currentOrgInFiltered) {
                    console.log('✅ Setting org unit in form data:', currentOrgInFiltered.name, currentOrgInFiltered.id);
                    setFormData(prev => ({
                        ...prev,
                        orgUnit: currentOrgInFiltered.id,
                        orgUnitName: currentOrgInFiltered.name
                    }));
                } else {
                    console.log('⚠️ Current org unit not in filtered list, keeping existing selection');
                }
            } else {
                // ✅ CRITICAL FIX: If org unit not in hierarchy, reset to empty
                console.log('⚠️ Existing org unit not in hierarchy, resetting to empty');
                setFormData(prev => ({
                    ...prev,
                    orgUnit: '',
                    orgUnitName: ''
                }));
            }
        }
    };


    const renderCascadeSelector = () => {
        if (!formData.userRole) return null;

        // ✅ NEW: Get effective level with fallback for missing levels
        const availableLevels = getAvailableLevels();
        const targetLevel = getEffectiveLevel(formData.userRole, availableLevels);
        if (!targetLevel) return null;

        const roleName = getRoleName(formData.userRole);


        const isCeoRole = roleName.toLowerCase().includes('chief') ||
            roleName.toLowerCase().includes('executive') ||
            roleName.toLowerCase().includes('ceo');

        if (!allOrgUnits.length || !filteredOrgUnits.length) {
            console.log('⏳ Waiting for org units to load...');
            return (
                <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontWeight: '400', marginBottom: '4px' }}>
                        Department <span style={{ color: 'red' }}>*</span>
                    </div>
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        color: '#666'
                    }}>
                        <CircularLoader small /> Loading department options...
                    </div>
                </div>
            );
        }

        const levelsToShow = availableLevels.filter(level => level <= targetLevel);

        // ==================== CEO ROLE SPECIAL HANDLING ====================
        if (isCeoRole) {
            console.log('CEO role detected:', roleName);

            // For CEO, we should only show the root org unit (TPC Limited)
            const rootOrgUnit = allOrgUnits.find(ou => ou.isRoot);

            if (!rootOrgUnit) {
                console.warn('⚠️ No root org unit found for CEO role');
                return (
                    <div style={{ marginBottom: '1rem' }}>
                        <NoticeBox error title="Configuration Error">
                            No organization unit found for CEO role. Please contact administrator.
                        </NoticeBox>
                    </div>
                );
            }

            // Auto-select the root org unit for CEO
            if (formData.orgUnit !== rootOrgUnit.id) {
                console.log('Auto-selecting root org unit for CEO:', rootOrgUnit.name);
                setFormData(prev => ({
                    ...prev,
                    orgUnit: rootOrgUnit.id,
                    orgUnitName: rootOrgUnit.name
                }));
            }

            return (
                <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                    }}>
                        <div style={{ fontWeight: '400' }}>
                            Department <span style={{ color: 'red' }}>*</span>
                        </div>

                    </div>

                    {/* CEO only gets the root org unit */}
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#e8f4fd',
                        borderRadius: '6px',
                        border: '2px solid #4a90e2',
                        marginBottom: '12px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>

                            <div>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                    Chief Executive Officer
                                </div>
                                <div>
                                    <strong>Organization:</strong> {rootOrgUnit.name}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '4px' }}>
                                    As CEO, you are assigned to the entire organization.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Hidden input to maintain form data */}
                    <input
                        type="hidden"
                        value={rootOrgUnit.id}
                        name="orgUnit"
                    />
                </div>
            );
        }

        // ==================== REGULAR ROLES ====================
        // For single level selection
        if (levelsToShow.length <= 1) {
            const availableOptions = getAvailableOptions(filteredOrgUnits, formData.orgUnit);
            const isValid = isValidSelection(formData.orgUnit, availableOptions);
            const safeSelectedValue = formData.orgUnit || "";

            return (
                <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                    }}>
                        <div style={{ fontWeight: '400' }}>
                            Department <span style={{ color: 'red' }}>*</span>
                        </div>
                    </div>

                    {/* In your renderCascadeSelector function, find the SingleSelect for org unit */}
                    <SingleSelect
                        selected={safeSelectedValue}
                        onChange={({ selected }) => {
                            console.log('📍 Department selected:', selected);
                            const selectedOrgUnit = allOrgUnits.find(ou => ou.id === selected);
                            if (selectedOrgUnit) {
                                setFormData(prev => ({
                                    ...prev,
                                    orgUnit: selected,
                                    orgUnitName: selectedOrgUnit.name
                                }));
                            }
                        }}
                        validationText={errors.orgUnit}
                        error={!!errors.orgUnit}
                        required
                        placeholder="Select a department..."
                        filterable
                        noMatchText="No matching department found"
                    >
                        <SingleSelectOption value="" label="– Select Department –" />
                        {availableOptions.map(orgUnit => (
                            <SingleSelectOption
                                key={orgUnit.id}
                                value={orgUnit.id}
                                label={`${orgUnit.name} (Level ${orgUnit.level})`}
                            />
                        ))}
                        {/* ✅ ADD THIS: Ensure the currently selected org unit is always in options */}
                        {formData.orgUnit && !availableOptions.some(ou => ou.id === formData.orgUnit) && (
                            <SingleSelectOption
                                value={formData.orgUnit}
                                label={`${formData.orgUnitName || 'Loading...'} (Selected)`}
                            />
                        )}
                    </SingleSelect>

                </div>
            );
        }

        // ==================== MULTI-LEVEL CASCADE ====================
        // For multi-level cascade
        const allAvailableOrgs = getOrgUnitsAtLevel(targetLevel);
        const availableOptions = getAvailableOptions(allAvailableOrgs, formData.orgUnit);
        const isValid = isValidSelection(formData.orgUnit, availableOptions);
        const safeSelectedValue = formData.orgUnit || "";

        return (
            <div style={{ marginBottom: '1rem' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                }}>
                    <div style={{ fontWeight: '400' }}>
                        Department <span style={{ color: 'red' }}>*</span>
                    </div>
                </div>
                {/* Current selection display */}
                {formData.orgUnit && (
                    <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }}>
                            <div>
                                <strong style={{ display: 'block', marginBottom: '4px' }}>
                                    Selected Department:
                                </strong>
                                <span style={{ fontSize: '1.1rem' }}>{formData.orgUnitName}</span>
                            </div>
                            {formData.orgUnit && allOrgUnits.find(ou => ou.id === formData.orgUnit) && (
                                <span style={{
                                    fontSize: '0.8em',
                                    color: '#fff',
                                    backgroundColor: '#4a90e2',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontWeight: '500'
                                }}>
                                    Level {allOrgUnits.find(ou => ou.id === formData.orgUnit).level}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Final department selector for the target level */}
                <SingleSelect
                    selected={safeSelectedValue}
                    onChange={({ selected }) => {
                        console.log('📍 Final department selected:', selected);
                        const selectedOrgUnit = allOrgUnits.find(ou => ou.id === selected);
                        if (selectedOrgUnit) {
                            setFormData(prev => ({
                                ...prev,
                                orgUnit: selected,
                                orgUnitName: selectedOrgUnit.name
                            }));

                            // Update cascade selections
                            setCascadeSelections(prev => ({
                                ...prev,
                                [targetLevel]: selected
                            }));
                        }
                    }}
                    validationText={errors.orgUnit}
                    error={!!errors.orgUnit}
                    required
                    placeholder="Select final department..."
                    filterable
                    noMatchText="No matching department found"
                >
                    <SingleSelectOption value="" label="– Select Final Department –" />
                    {availableOptions.map(orgUnit => (
                        <SingleSelectOption
                            key={orgUnit.id}
                            value={orgUnit.id}
                            label={`${orgUnit.name} (Level ${orgUnit.level})`}
                        />
                    ))}
                </SingleSelect>

            </div>
        );
    };

    const loadAllOrgUnits = async (rootOrgUnitId) => {
        try {
            console.log('🌐 Loading org units from user hierarchy...');

            // Get root org unit details
            const { rootOrg } = await engine.query({
                rootOrg: {
                    resource: `organisationUnits/${rootOrgUnitId}`,
                    params: { fields: 'id,name,level,path' }
                }
            });
            console.log('🏢 Root org unit:', rootOrg.name, 'Level:', rootOrg.level, 'ID:', rootOrg.id);

            // The ou parameter restricts to user's data capture org units
            const { organisationUnits } = await engine.query({
                organisationUnits: {
                    resource: 'organisationUnits',
                    params: {
                        fields: 'id,name,level,path,parent[id,name]',
                        // Use user's root org unit as the ou parameter
                        ou: rootOrgUnitId,
                        ouMode: 'DESCENDANTS',
                        // No level filter - we need all levels
                        order: 'name:asc',
                        paging: 'false'
                    }
                }
            });

            const fetchedUnits = organisationUnits.organisationUnits || [];
            console.log(`📊 Fetched ${fetchedUnits.length} org units from DHIS2 API`);

            // ✅ FILTER: Only include org units that are descendants of rootOrgUnitId
            // This ensures we ONLY show TPC Limited's hierarchy, not other companies
            let orgUnits = fetchedUnits
                .filter(ou => {
                    // Check if the org unit's path starts with rootOrg's path
                    // OR if the org unit IS the root org unit
                    const isDescendant = ou.path && (
                        ou.path === rootOrg.path ||
                        ou.path.startsWith(rootOrg.path + '/')
                    );
                    const isRoot = ou.id === rootOrgUnitId;
                    return isDescendant || isRoot;
                })
                .map(ou => ({
                    id: ou.id,
                    name: ou.name,
                    level: ou.level,
                    levelName: `Level ${ou.level}`,
                    parentId: ou.parent?.id || null,
                    path: ou.path,
                    isRoot: ou.id === rootOrgUnitId
                }));

            // Ensure root org unit is included
            if (!orgUnits.some(ou => ou.id === rootOrgUnitId)) {
                orgUnits.unshift({
                    id: rootOrg.id,
                    name: rootOrg.name,
                    level: rootOrg.level,
                    levelName: `Level ${rootOrg.level}`,
                    parentId: null,
                    path: rootOrg.path,
                    isRoot: true
                });
                console.log('➕ Added root org unit');
            }

            console.log(`✅ Loaded ${orgUnits.length} org units (ONLY in ${rootOrg.name} hierarchy)`);

            // ✅ DEBUG: Log all levels found
            const levels = [...new Set(orgUnits.map(ou => ou.level))].sort((a, b) => a - b);
            console.log('🔍 Levels found in hierarchy:', levels);

            // ✅ DEBUG: Log first few org units at each level
            levels.forEach(level => {
                const unitsAtLevel = orgUnits.filter(ou => ou.level === level).slice(0, 5);
                console.log(`🔍 Level ${level} sample (${unitsAtLevel.length}):`, unitsAtLevel.map(ou => ({ id: ou.id, name: ou.name })));
            });

            return orgUnits;
        } catch (error) {
            console.error('❌ Failed to load org units:', error);
            return [];
        }
    };


    useEffect(() => {
        let isMounted = true;
        const initializeUserData = async () => {
            try {
                const { me } = await engine.query({
                    me: {
                        resource: 'me',
                        params: { fields: 'organisationUnits[id,name,level,path]' }
                    }
                });

                if (!isMounted) return;

                if (me.organisationUnits && me.organisationUnits.length > 0) {
                    // ✅ CRITICAL: Find the HIGHEST level org unit (should be root/level 1)
                    const rootOrg = me.organisationUnits.reduce((highest, current) => {
                        return (current.level < highest.level) ? current : highest;
                    });

                    console.log('🏢 User org units:', me.organisationUnits);
                    console.log('🎯 Selected root org unit:', {
                        name: rootOrg.name,
                        level: rootOrg.level,
                        id: rootOrg.id
                    });

                    setUserOrgUnitId(rootOrg.id);
                    const orgUnits = await loadAllOrgUnits(rootOrg.id);

                    if (!isMounted) return;
                    setAllOrgUnits(orgUnits);
                    setOrgUnitsLoaded(true);

                    // Set default org unit in formData if needed
                    if (!formData.orgUnit) {
                        setFormData(prev => ({
                            ...prev,
                            orgUnit: rootOrg.id,
                            orgUnitName: rootOrg.name
                        }));
                    }
                }
            } catch (error) {
                console.error('Failed to initialize user data:', error);
                if (error.status === 401 || error.status === 403) {
                    showNotification('error', 'Authentication Required', 'You are not authorized to access this feature. Please log in with an admin account.');
                } else {
                    showNotification('error', 'Unexpected Error', 'Failed to load required data. Please try again or contact support.');
                }
                setOrgUnitsLoaded(true);
                setLoading(false);
            }
        };

        initializeUserData();
        return () => { isMounted = false; };
    }, [engine]);

    // Add this new useEffect to handle cascade initialization when org units become available
    useEffect(() => {
        // Only run if userOrgUnitId is set to avoid race conditions
        if (isEditMode && orgUnitsLoaded && formData.orgUnit && formData.userRole && allOrgUnits.length > 0 && userOrgUnitId) {
            console.log('🎯 Initializing role-based cascade for edit mode with role:', formData.userRole);
            console.log('🔍 [DEBUG] userOrgUnitId is set:', userOrgUnitId);

            // Re-run cascade initialization with the now-available org units
            initializeCascadeForEdit(allOrgUnits, formData.orgUnit, formData.userRole);
            handleUserTypeChange(formData.userRole);
        }
    }, [isEditMode, orgUnitsLoaded, formData.orgUnit, formData.userRole, allOrgUnits.length, userOrgUnitId]);



    useEffect(() => {
        console.log('🔄 Vacant positions effect triggered');
        const loadVacantPositions = async () => {
            try {
                console.log('🔍 Loading ALL accessible vacant positions (no org unit filtering)');


                const { positions } = await engine.query({
                    positions: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: 'UGGo0Ebjl0E',
                            ouMode: 'ACCESSIBLE',
                            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                            pageSize: 1000
                        }
                    }
                });

                console.log('✅ Positions API response:', positions);
                const allPositions = positions.trackedEntityInstances || [];
                console.log('📊 Total positions found:', allPositions.length);

                // Filter for vacant positions
                const vacant = allPositions.filter(pos => {
                    const attrs = extractAttributes(pos.attributes);
                    const existingStaffUserId = attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
                    const status = (attrs[POSITION_ATTRIBUTES.POSITION_STATUS] || '').toLowerCase();
                    const hasStaff = existingStaffUserId && existingStaffUserId.trim() !== '';
                    const isVacant = !hasStaff || status === 'vacant';

                    console.log('Position vacancy check:', {
                        title: attrs[POSITION_ATTRIBUTES.POSITION_TITLE],
                        positionId: attrs[POSITION_ATTRIBUTES.POSITION_ID],
                        existingStaffUserId,
                        status,
                        hasStaff,
                        isVacant
                    });

                    return isVacant;
                });

                console.log('✅ Vacant positions found:', vacant.length);
                setVacantPositions(vacant);
            } catch (error) {
                console.error('❌ Failed to load vacant positions:', error);
                setVacantPositions([]);
            }
        };

        loadVacantPositions();
    }, [engine]);  // ✅ Remove userOrgUnitId from dependencies

    // Handle initial data loading - FIXED: Ensure org unit is properly set from initial data
    const hasLoadedInitialData = useRef(false);

    // Helper to check for position IDs in various formats
    const findPositionIdInData = (data) => {
        console.log('🔍 Searching for position ID in data:', data);

        // Check direct properties
        if (data.positionId && data.positionId.trim() !== '') {
            console.log('✅ Found in data.positionId:', data.positionId);
            return data.positionId;
        }

        if (data.assignedToPosition && data.assignedToPosition.trim() !== '') {
            console.log('✅ Found in data.assignedToPosition:', data.assignedToPosition);
            return data.assignedToPosition;
        }

        // Check attributes
        if (data.attributes && Array.isArray(data.attributes)) {
            // Look for POSITION_ID attribute
            const positionAttr = data.attributes.find(attr =>
                attr.attribute === POSITION_ATTRIBUTES.POSITION_ID &&
                attr.value &&
                attr.value.trim() !== ''
            );

            if (positionAttr) {
                console.log('✅ Found in attributes[POSITION_ID]:', positionAttr.value);
                return positionAttr.value;
            }

            // Look for any TEI ID (11 character alphanumeric)
            const teiAttributes = data.attributes.filter(attr =>
                attr.value &&
                attr.value.trim() !== '' &&
                /^[a-zA-Z0-9]{11}$/.test(attr.value.trim())
            );

            if (teiAttributes.length > 0) {
                console.log('🔍 Possible TEI IDs found:', teiAttributes);
                // Return the first one that might be a position
                return teiAttributes[0].value;
            }
        }

        console.log('❌ No position ID found');
        return '';
    };

    // Replace your current initial data useEffect with this SIMPLIFIED version
    useEffect(() => {
        if (safeInitialData && !hasLoadedInitialData.current) {
            console.log('🎯 SIMPLIFIED INITIAL DATA LOADING');

            // SIMPLE position extraction - just get it from wherever it might be
            let positionId = '';

            // First check: direct properties
            positionId = safeInitialData.positionId || safeInitialData.assignedToPosition || '';

            // Second check: attributes
            if (!positionId && safeInitialData.attributes) {
                // Try the POSITION_ATTRIBUTES constant
                const positionAttr = safeInitialData.attributes.find(
                    attr => attr.attribute === POSITION_ATTRIBUTES.POSITION_ID
                );
                positionId = positionAttr?.value || '';

                // If still not found, try any attribute with "position" in the name
                if (!positionId) {
                    const anyPositionAttr = safeInitialData.attributes.find(attr =>
                        attr.attribute.toLowerCase().includes('position')
                    );
                    positionId = anyPositionAttr?.value || '';
                }
            }

            console.log('🎯 EXTRACTED positionId:', positionId);
            console.log('🎯 Is it a TEI ID?', /^[a-zA-Z0-9]{11}$/.test(positionId));

            // Set form data WITH positionId
            setFormData(prev => ({
                ...prev,
                // ... your other fields ...
                positionId: positionId,  // MAKE SURE THIS IS SET
                assignedToPosition: positionId
            }));

            // Set selected position
            if (positionId) {
                console.log('🎯 Setting selectedPosition to:', positionId);
                setSelectedPosition(positionId);
            }

            hasLoadedInitialData.current = true;
        }
    }, [safeInitialData]);

    useEffect(() => {
        console.log('🔄 POSITION ID CHANGED:', formData.positionId);

        if (formData.positionId && formData.positionId.trim() !== '') {
            console.log('🚀 Fetching position info for:', formData.positionId);
            fetchCurrentPositionInfo(formData.positionId);
        } else {
            console.log('🚫 No position ID to fetch');
            setCurrentPositionInfo(null);
        }
    }, [formData.positionId, fetchCurrentPositionInfo]);

    // Temporary debug effects SHOULD GO LATER
    useEffect(() => {
        console.log('🔍 FORM DATA UPDATED:', formData);
    }, [formData]);
    // Temporary debug effects SHOULD GO LATER
    useEffect(() => {
        console.log('🔍 FILTERED ORG UNITS UPDATED:', filteredOrgUnits);
    }, [filteredOrgUnits]);

    useEffect(() => {
        console.log('🔍 ROLE STATE:', {
            userRole: formData.userRole,
            userRoles: formData.userRoles,
            isArray: Array.isArray(formData.userRoles),
            length: formData.userRoles?.length || 0,
            hasValues: formData.userRoles?.some(r => r && r.trim() !== '')
        });
    }, [formData.userRole, formData.userRoles]);

    const handleChange = (field, value) => {
        console.log(`✏️ Field ${field} changed to:`, value);
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            return newData;
        });
        if (value) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName) newErrors.firstName = 'Required';
        if (!formData.gender) newErrors.gender = 'Required';
        if (!formData.phoneNumber) newErrors.phoneNumber = 'Required';
        if (!formData.email) newErrors.email = 'Required';
        if (!formData.userRoles || formData.userRoles.length === 0) {
            newErrors.userRole = 'At least one staff role is required';
        }

        // Add org unit validation
        if (!formData.orgUnit) newErrors.orgUnit = 'Please select a department';

        if (!formData.positionId && !selectedPosition) {
            newErrors.positionId = 'Please select a position for this staff member';
        }

        const hasRoles = (Array.isArray(formData.userRoles) && formData.userRoles.length > 0) ||
            formData.userRole;

        // Add department validation
        if (!formData.department && !formData.orgUnitName) {
            newErrors.department = 'Department information is required';
        }


        // Username validation for create mode
        const isCreateMode = mode === 'create' || mode === 'add';
        if (isCreateMode) {
            if (!formData.username) {
                newErrors.username = 'Username is required';
            } else if (!/^[a-zA-Z0-9_]{4,}$/.test(formData.username)) {
                newErrors.username = 'Username must be at least 4 characters (letters, numbers, underscores)';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

  const handleSubmit = async () => {
    console.log('🔄 handleSubmit called - mode:', mode);
    console.log('📋 Current formData:', {
        firstName: formData.firstName,
        userRoles: formData.userRoles,
        userRolesLength: formData.userRoles?.length,
        orgUnit: formData.orgUnit,
        username: formData.username,
        email: formData.email
    });

    if (!isMountedRef.current) {
        console.log('🚫 Component unmounted, skipping submit');
        return;
    }

    if (loading) {
        console.log('⏸️ Submission already in progress, skipping...');
        return;
    }

    // Run validation
    const isValid = validate();
    console.log('🔍 Validation result:', isValid, 'Errors:', errors);

    if (!isValid) {
        console.log('❌ Validation failed');

        // Show specific validation errors
        if (errors.userRole) {
            showNotification('error', 'Role Required', 'Please select at least one system role');
        }
        if (errors.orgUnit) {
            showNotification('error', 'Department Required', 'Please select a department');
        }
        if (errors.firstName) {
            showNotification('error', 'First Name Required', 'Please enter first name');
        }

        return;
    }

    setLoading(true);
    setNotification({ visible: false });

    try {
        const isCreateMode = mode === 'create' || mode === 'add';
        console.log('🎯 Starting submission process - mode:', isCreateMode ? 'CREATE' : 'EDIT');

        // Ensure userRoles is always an array
        const userRoles = Array.isArray(formData.userRoles) && formData.userRoles.length > 0
            ? formData.userRoles
            : formData.userRole
                ? [formData.userRole]
                : [];

        console.log('🎯 Processed userRoles:', userRoles);

        if (isCreateMode) {
            // ==================== CREATE MODE ====================
            console.log('📧 CREATE MODE: Creating new staff');

            // Get current user's org unit
            const currentUserOrgUnit = await getCurrentUserOrgUnit();

            const staffData = {
                firstName: formData.firstName,
                secondName: formData.secondName,
                surname: formData.secondName,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                username: formData.username,
                userRole: userRoles[0] || '', // For backward compatibility
                userRoles: userRoles,
                organisationUnits: [{ id: formData.orgUnit }], // Will be updated in sendUserInvitation
                orgUnit: formData.orgUnit,
                orgUnitName: formData.orgUnitName,
                gender: formData.gender,
                department: formData.department,
            };

            console.log('📧 Staff data for creation:', staffData);

            let userCreated = false;
            let invitationUrl = null;

            // Step 1: Send custom invitation with redirect URL
            console.log('📧 Step 1: Sending custom user invitation...');
            try {
              const invitationResult = await sendUserInvitation({
                    ...staffData,
                    frontendUrl: window.location.origin
                });

                console.log('✅ Custom invitation generated:', invitationResult);
                userCreated = true;

                // Store the invitation URL
                if (invitationResult.invitationUrl) {
                    setGeneratedInvitationUrl(invitationResult.invitationUrl);
                    setShowInvitationUrl(true);
                }

                showNotification(
                    'success', 
                    'Staff Created', 
                    `User ${formData.username} created. ${invitationResult.emailSent ? 'Invitation email sent.' : 'Check the invitation URL below.'}`,
                    {
                        showCopyButton: false,
                        invitationUrl: invitationResult.invitationUrl,
                        duration: 10000
                    }
                );

            } catch (invitationError) {
                console.error('❌ User invitation failed:', invitationError);

                if (invitationError.message.includes('409') ||
                    invitationError.message.includes('Conflict') ||
                    invitationError.message.includes('already taken')) {

                    console.log('⚠️ User may already exist, checking...');
                    const existingUser = await checkUserExistsByUsername(staffData.username, engine);

                    if (existingUser) {
                        console.log('✅ User already exists:', existingUser.id);
                        userCreated = true;
                        showNotification('info', 'Existing User',
                            `User ${staffData.username} already exists in the system.`);
                    } else {
                        showNotification('error', 'Invitation Failed',
                            `Unable to create user ${staffData.username}. Please try a different username.`);
                        setLoading(false);
                        return;
                    }
                } else {
                    showNotification('error', 'Invitation Failed',
                        `Could not send invitation: ${invitationError.message}`);
                    setLoading(false);
                    return;
                }
            }

            if (userCreated) {
                // Step 2: Create Staff TEI
                console.log('📝 Step 2: Creating Staff TEI...');
                try {
                    const teiResult = await createTEIOnly(engine, staffData, true);
                    console.log('✅ Staff TEI created:', teiResult);

                    if (teiResult.existingTei) {
                        console.log('⚠️ TEI already existed, using existing one');
                    }
                } catch (teiError) {
                    console.error('❌ Staff TEI creation failed:', teiError);
                    // Continue anyway - TEI creation is secondary
                }

                // Step 3: Update Position if selected
                if (selectedPosition) {
                    console.log('🔗 Step 3: Updating Position TEI...');
                    try {
                        const positionResult = await updatePositionLink(selectedPosition, formData.username);
                        console.log('✅ Position updated:', positionResult);
                    } catch (positionError) {
                        console.warn('⚠️ Position update failed:', positionError.message);
                        showNotification('warning', 'Position Update Failed',
                            `User created but position assignment failed: ${positionError.message}`);
                    }
                }

                // ✅ FIX: Show notification with copy button instead of auto-copying
                showNotification('success', 'Staff Created',
                    `User ${formData.username} created.`, {
                        showCopyButton: true,
                        copyText: invitationUrl,
                        message: invitationUrl ? 'Invitation URL generated. Click copy button below to copy it.' : ''
                    });

                // Alternative: Just log to console and let user copy manually
                if (invitationUrl) {
                    console.log('📋 Invitation URL (copy manually):', invitationUrl);
                }
            }

        } else {
            // ==================== EDIT MODE ====================
            console.log('✏️ EDIT MODE: Updating existing staff');

            try {
                await handleUpdateUser(userRoles);

                showNotification('success', 'Staff Updated',
                    `User ${formData.username} updated successfully with ${userRoles.length} role(s).`);

            } catch (updateError) {
                console.error('❌ Update failed:', updateError);

                if (updateError.message.includes('409') ||
                    updateError.message.includes('Conflict') ||
                    updateError.message.includes('TEI')) {

                    console.log('⚠️ TEI update conflict - user update may have succeeded');

                    showNotification('warning', 'Partial Update',
                        `User ${formData.username} updated but TEI may have issues: ${updateError.message}`);
                } else {
                    throw updateError;
                }
            }
        }

        // Call onSuccess callback after a delay
        await new Promise(resolve => setTimeout(resolve, 500));

        if (isMountedRef.current && onSuccess) {
            console.log('📞 Calling onSuccess callback');
            setTimeout(() => {
                if (isMountedRef.current) {
                    onSuccess();
                }
            }, 1500);
          }

        } catch (error) {
            console.error('❌ Submission failed:', error);
            showNotification('error', 'Submission Failed',
                error.message || 'An unexpected error occurred. Please try again.');

        } finally {
            if (isMountedRef.current) {
                console.log('🏁 Submission process completed');
                setLoading(false);
            }
        }
    };

    const extractAttributes = (attributes = []) => {
        const map = {};
        attributes.forEach((attr) => {
            map[attr.attribute] = attr.value;
        });
        return map;
    };
    // Function to unassign staff from position
    const handleUnassignPosition = async () => {
        if (!formData.positionId || !formData.username) {
            showNotification('error', 'Cannot Unassign', 'Missing position or staff information');
            return;
        }

        setLoading(true);
        try {
            console.log('🔓 Unassigning position:', formData.positionId);

            // Fetch current position
            const { position } = await engine.query({
                position: {
                    resource: `trackedEntityInstances/${formData.positionId}`,
                    params: {
                        fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
                    }
                }
            });

            if (!position) {
                throw new Error('Position not found');
            }

            // Update position attributes
            const currentAttrs = [...position.attributes];

            // Remove CURRENT_INCUMBENT
            const incumbentAttrIndex = currentAttrs.findIndex(
                attr => attr.attribute === POSITION_ATTRIBUTES.CURRENT_INCUMBENT
            );

            if (incumbentAttrIndex !== -1) {
                currentAttrs[incumbentAttrIndex].value = '';
            }

            // Update STATUS to "Vacant"
            const statusAttrIndex = currentAttrs.findIndex(
                attr => attr.attribute === POSITION_ATTRIBUTES.POSITION_STATUS
            );

            if (statusAttrIndex !== -1) {
                currentAttrs[statusAttrIndex].value = "Vacant";
            }

            // Save updated position
            await engine.mutate({
                resource: `trackedEntityInstances/${formData.positionId}`,
                type: 'update',
                data: {
                    trackedEntityInstance: formData.positionId,
                    orgUnit: position.orgUnit,
                    attributes: currentAttrs
                }
            });

            // Clear position from form data
            setFormData(prev => ({ ...prev, positionId: '' }));
            setSelectedPosition('');
            setCurrentPositionInfo(null);

            showNotification('success', 'Position Unassigned',
                `${formData.username} has been unassigned from the position.`);

        } catch (error) {
            console.error('❌ Failed to unassign position:', error);
            showNotification('error', 'Unassign Failed', error.message || 'Could not unassign position');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async (explicitRoles = null) => {
        // Use explicitRoles if provided, otherwise get from formData
        const userRoles = explicitRoles ||
            (Array.isArray(formData.userRoles) && formData.userRoles.length > 0
                ? formData.userRoles
                : formData.userRole
                    ? [formData.userRole]
                    : []);

        console.log('✏️ UPDATE - Using userRoles:', userRoles);

        // Get current user's org unit
        const currentUserOrgUnit = await getCurrentUserOrgUnit();

        // Prepare org units array
        const organisationUnits = [];

        // Add selected org unit
        if (formData.orgUnit) {
            organisationUnits.push({ id: formData.orgUnit });
        }

        // Add current user's org unit if different
        if (currentUserOrgUnit && currentUserOrgUnit.id !== formData.orgUnit) {
            console.log('➕ Adding current user org unit for update:', currentUserOrgUnit.name);
            organisationUnits.push({ id: currentUserOrgUnit.id });
        }

        const staffData = {
            firstName: formData.firstName,
            secondName: formData.secondName,
            surname: formData.secondName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            username: formData.username,
            userRoles: userRoles, // This should be an array
            organisationUnits: organisationUnits, // Now includes both org units
            orgUnit: formData.orgUnit,
            gender: formData.gender,
            department: formData.department,
            positionId: formData.positionId || selectedPosition,
        };

        console.log('✏️ Starting EDIT mode update with:', {
            userId: formData.userId,
            teiId: formData.id,
            userRoles: staffData.userRoles,
            orgUnits: organisationUnits.map(ou => ou.id)
        });

        try {
            if (safeInitialData.missingTEI) {
                console.log('👤 Staff without TEI - updating user first...');

                const result = await updateStaffComplete(engine, formData.userId, null, staffData);

                if (!result) {
                    throw new Error('Staff update failed: no response received from server');
                }

                console.log('✅ User updated successfully:', result);

                try {
                    console.log('🔍 Attempting to create Staff TEI record...');

                    const teiResult = await createTEIOnly(engine, {
                        ...staffData,
                        userId: formData.userId,
                        username: formData.username,
                        orgUnit: formData.orgUnit,
                        attributes: [
                            { attribute: "g3RRIfDiQra", value: formData.username },
                            { attribute: "fqqM9M2HOTS", value: formData.firstName },
                            ...(formData.gender ? [{ attribute: "R4tLPLErSmD", value: formData.gender }] : [])
                        ].filter(attr => attr.value && attr.value.trim() !== '')
                    });

                    console.log('✅ TEI operation completed:', teiResult);

                    if (teiResult.existingTei) {
                        showNotification('success', 'Staff Updated Successfully',
                            `User ${staffData.username} updated. Staff TEI record already exists.`);
                    } else {
                        showNotification('success', 'Staff Updated + TEI Created',
                            `User ${staffData.username} updated and Staff TEI record created successfully`);
                    }

                } catch (teiError) {
                    console.warn('⚠️ TEI creation failed but user was updated:', teiError.message);
                    showNotification('warning', 'User Updated - TEI Failed',
                        `User ${staffData.username} updated successfully but TEI creation failed: ${teiError.message}`);
                }
            } else {
                console.log('✅ Staff with TEI - updating via updateStaffComplete');

                const result = await updateStaffComplete(engine, formData.userId, formData.id, staffData);

                if (!result) {
                    throw new Error('Staff update failed: no response received from server');
                }

                console.log('✅ Update completed successfully:', result);

                // ==================== CRITICAL: Check if roles were updated ====================
                if (result.user?.userCredentials?.userRoles) {
                    console.log('🎯 Updated user roles:', result.user.userCredentials.userRoles);
                }

                if (result.tei?.error || result.note?.includes('TEI')) {
                    console.log('⚠️ TEI update had issues but user was updated');
                    showNotification('warning', 'Partial Update',
                        `User ${staffData.username} updated but TEI may have issues`);
                }

                const failedGroups = result.userGroups?.filter(group => !group.result.success) || [];
                if (failedGroups.length > 0) {
                    console.log('⚠️ Some user group assignments failed:', failedGroups.length);
                    showNotification('warning', 'Partial Update Complete',
                        `User ${staffData.username} updated successfully, but ${failedGroups.length} user group assignments failed and may need manual setup.`);
                } else {
                    showNotification('success', 'Staff updated successfully',
                        `User ${staffData.username} updated with ${staffData.userRoles?.length || 0} role(s) and assigned to ${organisationUnits.length} org unit(s)`);
                }

                return result;
            }

        } catch (error) {
            console.error('❌ Update failed:', error);

            if (error.message.includes('409') ||
                error.message.includes('Conflict') ||
                error.message.includes('c2TdETcCjWm') ||
                error.message.includes('TEI') ||
                error.message.includes('TEI_CONFLICT')) {

                console.log('🔄 TEI update conflict - user update likely succeeded');
                throw new Error('TEI_CONFLICT: ' + error.message);
            }

            throw error;
        }
    };
    // Update the username field to check for create mode
    const isCreateMode = mode === 'create' || mode === 'add';

    const renderFormField = (label, children, required = true) => (
        <div style={{ marginBottom: '5px' }}>
            <div style={{ fontWeight: '400', marginBottom: '4px' }}>
                {label} {required && <span style={{ color: 'red' }}>*</span>}
            </div>
            {children}
        </div>
    );

    if (optsLoading) return <p>Loading option sets...</p>;
    if (optsError) return <NoticeBox title="Error loading options">{optsError.message}</NoticeBox>;

    return (
        <div style={{ padding: '1.5rem' }}>
            {notification.visible && (
                    <div style={{ position: 'fixed', bottom: '20px', right: '200px', zIndex: 10000, width: '450px' }}>
                        <AlertBar
                            {...(notification.type === 'success' ? { success: true } :
                                notification.type === 'warning' ? { warning: true } :
                                    { critical: true })}
                            duration={notification.type === 'warning' ? 7000 : 5000}
                            onHidden={() => setNotification({ visible: false })}
                        >
                            <div>
                                <div><strong>{notification.title}</strong></div>
                                <div style={{ marginTop: '4px' }}>{notification.details}</div>                          
                                                                
                                {notification.message && (
                                    <div style={{ marginTop: '8px', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                        {notification.message}
                                    </div>
                                )}
                            </div>
                        </AlertBar>
                    </div>
                )}
            <div style={{
                background: 'white',
                borderRadius: '12px',
                backgroundColor: 'rgba(200, 243, 230, 0.1)',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                padding: '2rem',
                margin: '1rem 0',                
            }}>

                <h2 style={{ marginBottom: '1rem' }}>
                    {Object.keys(safeInitialData).length > 0 ? 'Edit Staff' : 'Add New Staff'}
                </h2>

                {/* NEW: Invitation notice for create mode */}
                {isCreateMode && (
                    <NoticeBox
                        title="User Invitation"
                        style={{ marginBottom: '1.5rem' }}
                    >
                        New users will receive an email invitation to complete their registration and set their password.
                        The system will automatically guide them through password setup and verification.
                    </NoticeBox>
                )}

                {/* NEW: Resend invitation option for edit mode */}
                    {isEditMode && (
                        <div style={{
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            backgroundColor: '#b2d8f5',
                            border: '1px solid #1e7eec',
                            borderRadius: '4px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <strong>User Invitation</strong>
                                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                        Resend invitation email if user hasn't completed registration
                                    </div>
                                </div>
                                <Button
                                    small
                                    onClick={handleResendInvitation}
                                    loading={loading}
                                    disabled={loading}
                                >
                                    Resend Invitation
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Manual Invitation URL Section - Only shown when URL is generated (MOVED OUTSIDE) */}
                    {showInvitationUrl && generatedInvitationUrl && (
                        <div style={{
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            backgroundColor: '#b2d8f5',
                            border: '2px solid #1e7eec',
                            borderRadius: '8px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <div>
                                    <strong style={{ fontSize: '1rem' }}>📋 Manual Invitation URL</strong>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                        Copy this URL and send it to the staff member manually if they didn't receive the email
                                    </div>
                                </div>
                                <Button
                                    small
                                    onClick={handleCopyInvitationUrl}
                                    style={{
                                        backgroundColor: copySuccess ? '#4CAF50' : '#4a90e2',
                                        color: 'white',
                                        border: 'none'
                                    }}
                                >
                                    {copySuccess ? '✓ Copied!' : 'Copy URL'}
                                </Button>
                            </div>
                            <div style={{
                                padding: '0.75rem',
                                backgroundColor: 'white',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                wordBreak: 'break-all'
                            }}>
                                {generatedInvitationUrl}
                            </div>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                                <strong>Note:</strong> This URL expires after 48 hours. The staff member will use it to set their password.
                            </div>
                        </div>
                    )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.2rem', marginTop: '20px' }}>

                    {renderFormField('First Name', (
                        <InputField
                            value={formData.firstName}
                            onChange={({ value }) => handleChange('firstName', value)}
                            validationText={errors.firstName}
                            error={!!errors.firstName}
                            required
                        />
                    ))}

                    {renderFormField('Surname', (
                        <InputField
                            value={formData.secondName}
                            onChange={({ value }) => handleChange('secondName', value)}
                        />
                    ))}

                    {renderFormField('Username', (
                        <div>
                            <InputField
                                value={formData.username}
                                onChange={({ value }) => {
                                    console.log('⌨️ Username input changed:', value);
                                    handleChange('username', value);
                                }}
                                validationText={
                                    formData.username.length > 0 && !usernameAvailable
                                        ? 'Username not available'
                                        : '4+ chars, letters, numbers, underscores'
                                }
                                error={!!errors.username || (formData.username.length > 0 && !usernameAvailable)}
                                required={isCreateMode}
                                disabled={!isCreateMode || loading}
                                loading={checkingUsername}
                            />
                            {errors.username && <div style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>{errors.username}</div>}
                            {usernameNotification && (
                                <div style={{
                                    color: usernameNotification.type === 'error' ? 'red' : 'green',
                                    fontSize: '0.8rem',
                                    marginTop: '4px',
                                    fontStyle: 'italic'
                                }}>
                                    {usernameNotification.message}
                                </div>
                            )}
                        </div>
                    ))}

                    {renderFormField('Email Address', (
                        <InputField
                            type="email"
                            value={formData.email}
                            onChange={({ value }) => handleChange('email', value)}
                            validationText={errors.email}
                            error={!!errors.email}
                            required
                        />
                    ))}

                    <div>
                        {renderFormField('Gender', (
                            <SingleSelect
                                selected={formData.gender}
                                onChange={({ selected }) => handleChange('gender', selected)}
                                validationText={errors.gender}
                                error={!!errors.gender}
                                required
                                filterable
                                noMatchText="No matching gender found"
                            >
                                <SingleSelectOption label="– Select Gender –" value="" />
                                {genderOps.map((o, index) => (
                                    <SingleSelectOption key={`gender-${o.code || o.id}-${index}`} value={o.code} label={o.name} />
                                ))}
                            </SingleSelect>
                        ))}
                    </div>

                    {renderFormField('Phone Number', (
                        <InputField
                            value={formData.phoneNumber}
                            onChange={({ value }) => handleChange('phoneNumber', value)}
                            validationText={errors.phoneNumber}
                            error={!!errors.phoneNumber}
                            required
                        />
                    ))}

                    <div>
                        {renderFormField('Assign System Role(s)', (
                            // In your render section, update the MultiSelect for roles:
                            <MultiSelect
                                selected={formData.userRoles}
                                onChange={({ selected }) => {
                                    if (!selected) return;
                                    const newRoles = Array.isArray(selected) ? selected : [selected];
                                    setFormData(prev => ({ ...prev, userRoles: newRoles }));
                                    setErrors(prev => ({ ...prev, userRole: undefined }));

                                    // Optional: Re-evaluate org unit access based on highest-level role
                                    const highestRole = newRoles.length
                                        ? newRoles.reduce((best, roleId) => {
                                            const bestLevel = ROLE_LEVEL_MAP[best] || 999;
                                            const currLevel = ROLE_LEVEL_MAP[roleId] || 999;
                                            return currLevel < bestLevel ? roleId : best;
                                        })
                                        : null;
                                    if (highestRole && newRoles.length > 0) {
                                        handleUserTypeChange(highestRole);
                                    }
                                }}
                                validationText={errors.userRole}
                                error={!!errors.userRole}
                                required
                                filterable
                                noMatchText="No matching role found"
                            >
                                <MultiSelectOption value="" label="Select role(s)" disabled />
                                {STAFF_ROLES_ID.map((role, index) => (
                                    <MultiSelectOption
                                        key={`${role.id}-${index}`}
                                        value={role.id}
                                        label={role.label || role.name || role.id} // Ensure label is shown
                                    />
                                ))}
                            </MultiSelect>
                        ))}
                    </div>
                    {formData.orgUnitName && !filteredOrgUnits.length && (
                        <div style={{
                            padding: '1rem',
                            backgroundColor: '#fff3cd',
                            borderRadius: '4px',
                            marginBottom: '1rem',
                            border: '1px solid #ffeaa7'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#856404' }}>
                                ⚠️ Department Information (Loading...)
                            </div>
                            <div style={{ fontSize: '0.9rem' }}>
                                <div><strong>Current Department:</strong> {formData.orgUnitName}</div>
                                <div style={{ marginTop: '4px', color: '#666', fontSize: '0.85rem' }}>
                                    <em>Department options are being loaded. Please wait...</em>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Cascade Department Unit Selection */}
                    {renderCascadeSelector()}

                  
                     {/* Position Assignment Section - Always visible, outside the grid */}
                    <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ fontWeight: '400', marginBottom: '8px' }}>
                        Assign to Position <span style={{ color: 'red' }}>*</span>
                        <span style={{ color: '#999', fontSize: '0.85rem', marginLeft: '4px' }}>
                            (Required - Staff must be assigned to a position)
                        </span>
                    </div>

                    {loadingPositionInfo ? (
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <CircularLoader small /> Loading position information...
                        </div>
                    ) : (formData.positionId || selectedPosition) ? (
                        // ✅ Show chip whenever a position ID exists (even if details are missing)
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                            }}>
                                <div style={{ fontWeight: '500' }}>
                                    Currently Assigned Position
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                    {currentPositionInfo?.isVacant ? 'Vacant' : 'Occupied'}
                                </div>
                            </div>

                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: '#e8f4fd',
                                borderRadius: '8px',
                                border: '1px solid #4a90e2',
                                marginBottom: '12px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#2c3e50' }}>
                                            {currentPositionInfo?.title || 'Position (loading failed)'}
                                        </div>
                                        <div style={{ fontSize: '0.9rem', color: '#555' }}>
                                            Position ID: {currentPositionInfo?.positionId || formData.positionId || selectedPosition}
                                        </div>
                                        {currentPositionInfo?.currentIncumbent && (
                                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                                Current Incumbent: {currentPositionInfo.currentIncumbent}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {!currentPositionInfo?.isVacant && currentPositionInfo?.currentIncumbent && (
                                            <Button
                                                small
                                                onClick={() => {
                                                    if (window.confirm(`Do you want to unassign ${formData.username} from this position?`)) {
                                                        handleUnassignPosition();
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: '#ff6b6b',
                                                    color: 'white'
                                                }}
                                            >
                                                Unassign
                                            </Button>
                                        )}
                                        <Button
                                            small
                                            secondary
                                            onClick={() => {
                                                setCurrentPositionInfo(null);
                                                setSelectedPosition('');
                                                setFormData(prev => ({ ...prev, positionId: '' }));
                                            }}
                                        >
                                            Change
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // ✅ Show vacant positions dropdown only when NO position is assigned
                        <SingleSelect
                            selected={selectedPosition || formData.positionId || ''}
                            onChange={({ selected }) => {
                                console.log('🎯 Position selected:', selected);
                                if (selected && selected.trim() !== '') {
                                    const matchingPosition = vacantPositions.find(
                                        pos => pos.trackedEntityInstance === selected
                                    );
                                    if (matchingPosition) {
                                        console.log('✅ Valid position TEI selected:', selected);
                                        setSelectedPosition(selected);
                                        setFormData(prev => ({ ...prev, positionId: selected }));
                                        setErrors(prev => ({ ...prev, positionId: null }));
                                    } else {
                                        console.warn('⚠️ Invalid selection - not a position TEI:', selected);
                                    }
                                } else {
                                    setSelectedPosition('');
                                    setFormData(prev => ({ ...prev, positionId: '' }));
                                }
                            }}
                            placeholder="Select a vacant position (Required)"
                            filterable
                            noMatchText="No vacant positions available"
                            validationText={errors.positionId}
                            error={!!errors.positionId}
                            required
                        >
                            <SingleSelectOption value="" label="— Select a position —" />
                            {vacantPositions.length > 0 ? (
                                vacantPositions.map(pos => {
                                    const attrs = extractAttributes(pos.attributes);
                                    return (
                                        <SingleSelectOption
                                            key={pos.trackedEntityInstance}
                                            value={pos.trackedEntityInstance}
                                            label={`${attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Untitled'} (${attrs[POSITION_ATTRIBUTES.POSITION_ID] || 'No ID'})`}
                                        />
                                    );
                                })
                            ) : (
                                <SingleSelectOption 
                                    value="" 
                                    label="No vacant positions available" 
                                    disabled
                                />
                            )}
                        </SingleSelect>
                    )}
                    
                    {/* Show warning if no vacant positions available and no position assigned */}
                    {vacantPositions.length === 0 && !(formData.positionId || selectedPosition) && (
                        <NoticeBox 
                            warning 
                            title="No Vacant Positions" 
                            style={{ marginTop: '8px' }}
                        >
                            There are currently no vacant positions available. Please create a position first before adding staff.
                        </NoticeBox>
                    )}
                </div>
                
                </div>

                <div style={{ marginTop: '2rem', display: 'flex', gap: '1.5rem' }}>
                    <Button primary
                        onClick={handleSubmit}
                        disabled={loading}>
                        {loading ? (
                            <CircularLoader small />
                        ) : (
                            mode === 'edit' ? 'Save Changes' : '+ Invite Staff'
                        )}
                    </Button>
                    <button
                        onClick={onCancel}
                        style={{
                            backgroundColor: "#fcc10dff",
                            color: "#000",
                            border: 'none',
                            boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                            borderRadius: '5px',
                            width: '100px',
                            color: '#ffffffff',
                            cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>

                </div>
            </div>
        </div>
    );
};

export default StaffForm;
